import { el } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { formatRelative } from '../../core/format.js';
import { button } from '../../components/button.js';
import { errorPanel } from '../../components/error.js';
import { toast } from '../../components/toast.js';
import { memoryGroup, shortCode, sourceBadge, memoryLifeBadge } from './model.js';
import { SEARCH_WAIT, later, mergeRows } from './paging.js';
import { statusNote } from './status.js';

const TABS = [
  { id: 'fact', label: 'Facts', prefix: 'F' },
  { id: 'decision', label: 'Decisions', prefix: 'D' },
  { id: 'todo', label: 'To-dos', prefix: 'T' },
];

const PREVIEW = 2;

/**
 * Facts, decisions, and to-dos. Instructions are a separate list.
 * @param {{
 *   lists: Record<string, { rows: object[], total: number, loaded: boolean }>,
 *   counts: { fact: number, decision: number, todo: number },
 *   tab: string, query: string, search: { rows: object[], total: number } | null,
 *   status: string, error: string, canEdit: boolean, memoryV2?: boolean,
 *   permanentOnly?: boolean,
 *   onTab: (type: string) => void,
 *   onPermanent: (on: boolean) => void,
 *   onEnsure: (type: string) => Promise<{ ok: boolean, error?: string }>,
 *   onMore: (type: string) => Promise<{ ok: boolean, error?: string }>,
 *   onSearch: (q: string, offset: number) => Promise<{ ok: boolean, error?: string, rows: object[], total: number }>,
 *   onQuery: (q: string) => void,
 *   onSaved: (memory: object) => void,
 *   onDeleted: (id: string) => void,
 *   onRefresh: () => void,
 * }} opts
 */
export function renderMemory(opts) {
  if (opts.status === 'error') {
    return errorPanel({
      title: 'Memory could not be loaded',
      message: opts.error || 'Try again.',
      action: button('Retry', { onClick: opts.onRefresh }),
    });
  }
  const section = el('section', { class: 'kb-rv' });
  let tab = opts.tab || 'fact';
  let reviewAll = false;
  let editingId = '';
  let query = opts.query || '';
  let searching = Boolean(opts.search);
  let searchRows = opts.search ? opts.search.rows.slice() : [];
  let searchTotal = opts.search ? opts.search.total : 0;
  let busy = false;
  let ticket = 0;
  let permanentOnly = !!opts.permanentOnly;
  const memoryV2 = !!opts.memoryV2;
  const head = el('div');
  const body = el('div');
  const searchBox = el('input', {
    class: 'kb-search kb-mem-search',
    type: 'search',
    placeholder: 'Search memories',
    'aria-label': 'Search memories',
    value: query,
    onInput(event) {
      query = event.currentTarget.value;
      runSearch();
    },
  });
  const runSearch = later(() => lookup(query), SEARCH_WAIT);
  section.append(head, searchBox, body);
  paint();
  return section;

  function paint() {
    paintHead();
    paintBody();
  }

  function paintHead() {
    const tabs = el('div', { class: 'kb-mem-tabs', role: 'tablist', 'aria-label': 'Memory type' },
      TABS.map((item) => el('button', {
        class: tab === item.id && !searching ? 'kb-mtab is-on' : 'kb-mtab',
        type: 'button',
        role: 'tab',
        'aria-selected': tab === item.id && !searching ? 'true' : 'false',
        onClick: () => selectTab(item.id),
      }, item.label, ' ', el('span', null, String(opts.counts[item.id] || 0)))));
    const chips = [];
    if (memoryV2 && opts.canEdit) {
      chips.push(el('button', {
        class: permanentOnly ? 'kb-chip kb-filter is-on' : 'kb-chip kb-filter',
        type: 'button',
        'aria-pressed': permanentOnly ? 'true' : 'false',
        onClick: () => togglePermanent(),
      }, 'Permanent (AI)'));
    }
    head.replaceChildren(el('div', { class: 'kb-mem-head' },
      el('div', { class: 'kb-mem-title' },
        el('span', { class: 'kb-kicker' }, 'Memory'),
        chips.length ? el('div', { class: 'kb-mem-filters' }, chips) : null),
      tabs));
  }

  function paintBody() {
    const view = current();
    const nodes = [list(view.rows)];
    const links = footer(view);
    if (links) nodes.push(links);
    body.replaceChildren(...nodes);
  }

  function current() {
    if (searching) return { rows: searchRows, total: searchTotal };
    const list = opts.lists[tab] || { rows: [], total: 0 };
    return { rows: list.rows || [], total: opts.counts[tab] || list.total || 0 };
  }

  function list(rows) {
    if (!rows.length) {
      return searching
        ? statusNote('No memories match', 'Try a different search.')
        : permanentOnly
          ? statusNote('No permanent memories', 'When Bonnie marks a fact or decision permanent, it shows up here.')
          : statusNote('Nothing in this group', 'Memories show up after a chat, or when Bonnie reads a document.');
    }
    const shown = searching || reviewAll ? rows : rows.slice(0, PREVIEW);
    return el('div', { class: 'kb-rows kb-glass' }, shown.map((memory) => row(memory)));
  }

  function footer(view) {
    const links = [];
    const more = view.rows.length < view.total && (searching || reviewAll);
    if (more) links.push(linkButton(busy ? 'Loading…' : 'Load more', () => loadMore()));
    if (!searching && view.total > PREVIEW) {
      const label = reviewAll ? 'Show fewer' : `Review all ${view.total} `;
      links.push(linkButton(label, () => { reviewAll = !reviewAll; editingId = ''; paintBody(); }, !reviewAll));
    }
    if (!links.length) return null;
    return el('div', { class: 'kb-more' }, links);
  }

  function linkButton(label, onClick, arrow) {
    return el('button', {
      class: 'kb-link',
      type: 'button',
      disabled: busy ? true : null,
      onClick,
    }, label, arrow ? el('span', { class: 'arw' }, '→') : null);
  }

  async function selectTab(type) {
    tab = type;
    opts.onTab(type);
    reviewAll = false;
    editingId = '';
    clearSearch();
    const list = opts.lists[type];
    if (list && !list.loaded && (opts.counts[type] || 0) > 0) {
      busy = true;
      paintHead();
      body.replaceChildren(statusNote('Loading memories', ''));
      const result = await opts.onEnsure(type);
      busy = false;
      if (!result.ok) toast(result.error || 'Could not load memories', 'error');
    }
    paint();
  }

  async function togglePermanent() {
    permanentOnly = !permanentOnly;
    if (opts.onPermanent) opts.onPermanent(permanentOnly);
    reviewAll = false;
    editingId = '';
    clearSearch();
    busy = true;
    paintHead();
    body.replaceChildren(statusNote('Loading memories', ''));
    const result = await opts.onEnsure(tab);
    busy = false;
    if (!result.ok) toast(result.error || 'Could not load memories', 'error');
    paint();
  }

  function clearSearch() {
    ticket += 1;
    query = '';
    searchBox.value = '';
    searching = false;
    searchRows = [];
    searchTotal = 0;
    opts.onClear();
  }

  async function lookup(q) {
    const mine = ++ticket;
    const text = q.trim();
    if (!text) {
      searching = false;
      opts.onClear();
      paint();
      return;
    }
    const page = await opts.onSearch(text, 0);
    if (mine !== ticket || !section.isConnected) return;
    if (!page.ok) {
      toast(page.error || 'Could not search memories', 'error');
      return;
    }
    searching = true;
    reviewAll = false;
    editingId = '';
    searchRows = page.rows;
    searchTotal = page.total;
    opts.onRemember(text, { rows: searchRows, total: searchTotal });
    paint();
  }

  async function loadMore() {
    if (busy) return;
    busy = true;
    paintBody();
    const result = searching
      ? await opts.onSearch(query.trim(), searchRows.length)
      : await opts.onMore(tab);
    busy = false;
    if (!section.isConnected) return;
    if (!result.ok) {
      toast(result.error || 'Could not load memories', 'error');
      paintBody();
      return;
    }
    if (searching) {
      searchRows = mergeRows(searchRows, result.rows);
      searchTotal = result.total;
      opts.onRemember(query.trim(), { rows: searchRows, total: searchTotal });
    }
    paintBody();
  }

  function badges(memory) {
    if (!memoryV2) return null;
    const nodes = [];
    const source = sourceBadge(memory);
    if (source) nodes.push(el('span', { class: 'kb-badge is-source' }, source));
    const life = memoryLifeBadge(memory);
    if (life) {
      nodes.push(el('span', {
        class: `kb-badge is-${life.kind}`,
        title: life.title || null,
      }, life.label));
    }
    if (!nodes.length) return null;
    return el('div', { class: 'kb-mem-badges' }, nodes);
  }

  function row(memory) {
    if (editingId === memory.id) return editor(memory);
    const actions = [];
    if (opts.canEdit && memoryV2) {
      actions.push(el('button', {
        class: memory.pinned ? 'kb-btn is-on' : 'kb-btn',
        type: 'button',
        title: memory.pinned ? 'Unpin' : 'Pin',
        onClick: () => setPinned(memory, !memory.pinned),
      }, memory.pinned ? 'Unpin' : 'Pin'));
      if (memory.permanence === 'permanent' && !memory.pinned) {
        actions.push(el('button', {
          class: 'kb-btn',
          type: 'button',
          onClick: () => makeExpiring(memory),
        }, 'Make expiring'));
      }
    }
    if (opts.canEdit) {
      actions.push(el('button', {
        class: 'kb-btn',
        type: 'button',
        onClick() { editingId = memory.id; paintBody(); },
      }, 'Edit'));
    }
    return el('div', { class: 'kb-row' },
      el('span', { class: 'kb-code' }, shortCode(memory.id, prefixFor(memory))),
      el('div', { class: 'kb-row-body' },
        el('p', null, memory.content || memory.title || ''),
        badges(memory),
        el('span', { class: 'kb-when' }, whenLabel(memory.created_at))),
      actions.length ? el('div', { class: 'kb-row-actions' }, actions) : null);
  }

  function editor(memory) {
    const input = el('textarea', { rows: '3' }, memory.content || '');
    return el('div', { class: 'kb-row' },
      el('span', { class: 'kb-code' }, shortCode(memory.id, prefixFor(memory))),
      el('div', { class: 'kb-row-body' },
        input,
        el('div', { class: 'kb-actions' },
          el('button', { class: 'kb-btn kb-btn-fill', type: 'button', onClick: () => persist(memory, input.value) }, 'Save'),
          el('button', { class: 'kb-btn', type: 'button', onClick: () => remove(memory.id) }, 'Delete'),
          el('button', { class: 'kb-btn', type: 'button', onClick() { editingId = ''; paintBody(); } }, 'Cancel'))));
  }

  async function setPinned(memory, pinned) {
    const result = await api(`/context/memory/${encodeURIComponent(memory.id)}`, {
      method: 'PUT',
      body: { pinned },
    });
    if (result && result.error) {
      toast(result.error, 'error');
      return;
    }
    if (result && result.memory && opts.onSaved) opts.onSaved(result.memory);
    toast(pinned ? 'Memory pinned' : 'Memory unpinned');
    opts.onRefresh();
  }

  async function makeExpiring(memory) {
    const result = await api(`/context/memory/${encodeURIComponent(memory.id)}`, {
      method: 'PUT',
      body: { permanence: 'expiring' },
    });
    if (result && result.error) {
      toast(result.error, 'error');
      return;
    }
    if (result && result.memory && opts.onSaved) opts.onSaved(result.memory);
    toast('Marked expiring');
    opts.onRefresh();
  }

  async function persist(memory, raw) {
    const content = String(raw || '').trim();
    const result = content
      ? await api(`/context/memory/${encodeURIComponent(memory.id)}`, { method: 'PUT', body: { content } })
      : await api(`/context/memory/${encodeURIComponent(memory.id)}`, { method: 'DELETE' });
    if (result && result.error) {
      toast(result.error, 'error');
      return;
    }
    if (content && result && result.memory && opts.onSaved) opts.onSaved(result.memory);
    if (!content && opts.onDeleted) opts.onDeleted(memory.id);
    if (content && (!result || !result.memory)) {
      toast('Memory could not be saved', 'error');
      return;
    }
    toast(content ? 'Memory saved' : 'Memory deleted');
    editingId = '';
    opts.onRefresh();
  }

  async function remove(id) {
    const result = await api(`/context/memory/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (result && result.error) {
      toast(result.error, 'error');
      return;
    }
    if (opts.onDeleted) opts.onDeleted(id);
    toast('Memory deleted');
    editingId = '';
    opts.onRefresh();
  }
}

function whenLabel(iso) {
  const when = formatRelative(iso);
  if (!when || when === '—') return '';
  if (when === 'now') return 'Just now';
  return `About ${when} ago`;
}

function prefixFor(memory) {
  const group = memoryGroup(memory.memory_type);
  if (group === 'decision') return 'D';
  if (group === 'todo') return 'T';
  return 'F';
}
